import type {
  Asistente,
  Category,
  RegisterData,
  PrefixUpdateData,
  ApproveCategoryData,
  ChangeCategoryData,
} from "../types"
import type { ApiResponse, DynamicUser, DynamicSchema, DynamicCreateRequest, DynamicCreateResponse, DynamicUpdatePayload } from "../types/dynamic"


// Global API operation tracking
let apiOperationCallback: ((isActive: boolean) => void) | null = null

export const setApiOperationCallback = (callback: (isActive: boolean) => void) => {
  apiOperationCallback = callback
}
const withApiTracking = async <T>(operationName: string, apiCall: () => Promise<T>): Promise<T> => {
  try {
    console.log(`🌐 Iniciando operación API: ${operationName}`)
    apiOperationCallback?.(true)
    const result = await apiCall()
    console.log(`✅ Operación API completada: ${operationName}`)
    return result
  } catch (error) {
    console.error(`❌ Error en operación API ${operationName}:`, error)
    throw error
  } finally {
    apiOperationCallback?.(false)
  }
}

export const registerSimple = async (
  data: RegisterData,
): Promise<{
  success: boolean
  asistente_id?: string
  folio?: string
  category?: Category
  error?: string
}> => {
  return withApiTracking("registerSimple", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/asistentes/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })

    const result = await response.json()

    if (result.status !== 201) {
      throw new Error(result.message || "Error registrando asistente")
    }

    return { success: true, asistente_id: result.data?.asistente_id, folio: result.data?.folio }
  })
}

export const updatePrefix = async (data: PrefixUpdateData): Promise<void> => {
  return withApiTracking("updatePrefix", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/asistentes/update-prefix`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })

    const result = await response.json()

    if (result.status !== 200) {
      throw new Error(result.message || "Error actualizando prefijo")
    }
  })
}

export const updateAsistente = async (asistente: Asistente): Promise<void> => {
  return withApiTracking("updateAsistente", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/asistentes/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(asistente),
    })

    const result = await response.json()

    if (result.status !== 200) {
      throw new Error(result.message || "Error actualizando asistente")
    }
  })
}

export const approveCategory = async (data: ApproveCategoryData): Promise<void> => {
  return withApiTracking("approveCategory", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/asistentes/approve-category`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ asistente_id: Number.parseInt(data.asistente_id) }),
    })

    const result = await response.json()

    if (result.status !== 200) {
      throw new Error(result.message || "Error aprobando categoría")
    }
  })
}

export const changeCategory = async (data: ChangeCategoryData): Promise<void> => {
  return withApiTracking("changeCategory", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/asistentes/change-category`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        asistente_id: Number.parseInt(data.asistente_id),
        category_id: data.category_id,
      }),
    })

    const result = await response.json()

    if (result.status !== 200) {
      throw new Error(result.message || "Error cambiando categoría")
    }
  })
}

// =============================================================================
// DYNAMIC SYSTEM API FUNCTIONS
// =============================================================================

const DYNAMIC_API_BASE_URL = import.meta.env.VITE_API_URL as string

/**
 * Fetches dynamic users and schema from the new API endpoint
 */
export const fetchDynamicData = async (): Promise<ApiResponse> => {
  const MAX_RETRIES = 3
  const INITIAL_TIMEOUT = 30000 // 30 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      console.log(`🌐 Obteniendo datos dinámicos del nuevo API (intento ${attempt + 1})...`)

      const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando datos dinámicos (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: ApiResponse = await response.json()

      // Validar la estructura de la respuesta
      if (!data.data || !data.data.schema || !Array.isArray(data.data.users)) {
        throw new Error("Estructura de respuesta inválida del API dinámico")
      }

      console.log(`✅ Datos dinámicos obtenidos: ${data.data.users.length} usuarios, ${Object.keys(data.data.schema).length} campos de schema`)
      return data

    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1) // Backoff exponencial
        console.log(`⏳ Reintentando datos dinámicos (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch dynamic data after retries')
}

/**
 * Updates a dynamic user via API
 */
export const updateDynamicUser = async (user: DynamicUser): Promise<void> => {
  return withApiTracking("updateDynamicUser", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/users/${user.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(user),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (result.status !== 200) {
      throw new Error(result.message || "Error updating dynamic user")
    }
  })
}


/**
 * Creates one or multiple dynamic users using the specific API format
 */
export const createDynamicUser = async (createRequest: DynamicCreateRequest | DynamicCreateRequest[]): Promise<DynamicCreateResponse | DynamicUser[]> => {
  return withApiTracking("createDynamicUser", async () => {
    const isArray = Array.isArray(createRequest)
    console.log(`🌐 Creando ${isArray ? 'múltiples usuarios' : 'usuario'} dinámico${isArray ? 's' : ''}:`, createRequest)

    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(createRequest),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()

    if (isArray) {
      // Para array, esperamos una respuesta con array de usuarios creados
      if (result.status !== 201 && result.status !== 200) {
        throw new Error(result.message || "Error creating dynamic users")
      }

      // La respuesta puede ser directamente un array o tener la estructura { data: users }
      const users = Array.isArray(result) ? result : (result.data || result.users || [])
      console.log(`✅ ${users.length} usuarios dinámicos creados exitosamente`)
      return users
    } else {
      // Para objeto único, mantenemos la respuesta original
      if (result.status !== 201) {
        throw new Error(result.message || "Error creating dynamic user")
      }

      console.log('✅ Usuario dinámico creado exitosamente:', result.data)
      return result
    }
  })
}

/**
 * Updates a dynamic user with only changed fields using unique field identifier
 */
export const updateDynamicUserByUniqueField = async (payload: DynamicUpdatePayload): Promise<void> => {
  return withApiTracking("updateDynamicUserByUniqueField", async () => {
    console.log('🌐 Actualizando usuario dinámico:', payload.uniqueFieldValue, payload.updateData)

    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/users/${payload.uniqueFieldValue}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload.updateData),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (result.status !== 200) {
      throw new Error(result.message || "Error updating dynamic user")
    }

    console.log('✅ Usuario dinámico actualizado exitosamente')
  })
}

/**
 * Legacy function wrapper for backward compatibility
 * This allows existing code to continue working while we transition
 */
export const fetchDynamicUsers = async (): Promise<{ users: DynamicUser[], schema: DynamicSchema }> => {
  const response = await fetchDynamicData()
  return {
    users: response.data.users,
    schema: response.data.schema
  }
}

// =============================================================================
// LEGACY API FUNCTIONS (for backward compatibility)
// =============================================================================

/**
 * Fetches asistentes from the API
 */
export const fetchAsistentes = async (): Promise<Asistente[]> => {
  return withApiTracking("fetchAsistentes", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/asistentes`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data || []
  })
}

/**
 * Fetches groups from the API
 */
export const fetchGroups = async (): Promise<Record<string, string[]>> => {
  return withApiTracking("fetchGroups", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/groups`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data || {}
  })
}

/**
 * Fetches prefixes from the API
 */
export const fetchPrefixes = async (): Promise<Array<{ id: number; texto: string }>> => {
  return withApiTracking("fetchPrefixes", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/prefixes`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data || []
  })
}

/**
 * Fetches categories from the API
 */
export const fetchCategories = async (): Promise<Array<{ id: number; description: string; price: string }>> => {
  return withApiTracking("fetchCategories", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/categories`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data || []
  })
}

/**
 * Fetches coupon prefixes from the API
 */
export const fetchCouponPrefixes = async (): Promise<Array<{ id: number; prefix: string }>> => {
  return withApiTracking("fetchCouponPrefixes", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/coupon-prefixes`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.data || []
  })
}

/**
 * Sends payment link email to asistente
 */
export const sendPaymentLinkEmail = async (asistenteId: string, email: string): Promise<void> => {
  return withApiTracking("sendPaymentLinkEmail", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/asistentes/send-payment-link`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ asistente_id: asistenteId, email }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (result.status !== 200) {
      throw new Error(result.message || "Error sending payment link email")
    }
  })
}
